import { clone } from 'lodash';

import NoopOnReconnectGateway from './noop_on_reconnect_gateway';

const TEAM_ASSIST_ENDPOINT = {
  addUrl: '/api/v1/orgs/:orgId/agents/:agentId/customers/:customerId/team-assists',
  broadcastDeleteTopic: 'v1/orgs/:orgId/agents/:agentId/customers/:customerId/team-assists/event/delete',
  broadcastTopic: 'v1/orgs/:orgId/agents/:agentId/customers/:customerId/team-assists/:teamAssistId',
  collectionBroadcastDeleteTopic: 'v1/orgs/:orgId/agents/:agentId/customers/:customerId/team-assists/event/delete',
  collectionBroadcastTopic: 'v1/orgs/:orgId/agents/:agentId/customers/:customerId/team-assists/+',
  deleteUrl: '/api/v1/orgs/:orgId/agents/:agentId/customers/:customerId/team-assists',
  fetchAllUrl: '/api/v1/orgs/:orgId/agents/:agentId/customers/:customerId/team-assists',
  fetchUrl: '/api/v1/orgs/:orgId/agents/:agentId/customers/:customerId/team-assists/:teamAssistId',
  replaceUrl: '/api/v1/orgs/:orgId/agents/:agentId/customers/:customerId/team-assists/:teamAssistId',
  retryUrl: '/api/v1/orgs/:orgId/agents/:agentId/customers/:customerId/team-assists/:messageId/retry',
  updateUrl: '/api/v1/orgs/:orgId/agents/:agentId/customers/:customerId/team-assists/:teamAssistId',
};

/**
 * Gateway for team assist (customer team assist) API.
 * Extends NoopOnReconnectGateway with a retry endpoint for re-running the LLM
 * on a suggested response message.
 */
export default class TeamAssistGateway extends NoopOnReconnectGateway {
  constructor(backend) {
    super(backend, TEAM_ASSIST_ENDPOINT);
  }

  /**
   * POST a question. Override of the base `add` to append `?async=true`.
   * Supernova defaults this endpoint to a synchronous response shape so
   * stale agent-desktop tabs on the pre-async bundle keep receiving AI
   * messages inline; sending `async=true` opts this build into the
   * fire-and-forget path. Remove once supernova drops the sync branch.
   */
  add(params, data, httpOptions) {
    const localParams = clone(params);
    const url = this.buildUrl(this.endpoint.set(localParams).addUrl, { async: 'true' });
    const resource = this.endpoint.patterns.addUrl;

    return this.http
      .post(url, data || localParams, httpOptions, resource)
      .then(dto => {
        this.observer.onAddSuccess(dto, localParams);
        return dto;
      })
      .catch(error => {
        this.observer.onAddError(error, localParams);
        throw error;
      });
  }

  /**
   * POST to the retry endpoint for a given message. The backend re-runs the LLM
   * and returns the updated message (and may broadcast it).
   *
   * @param {Object} params - { customerId, messageId }
   * @param {Object} [httpOptions] - optional axios options
   * @returns {Promise} - resolves with the updated message DTO
   */
  retry(params, httpOptions) {
    const localParams = clone(params);
    const url = this.endpoint.set(localParams).retryUrl;
    const resource = this.endpoint.patterns.retryUrl;

    return this.http
      .post(url, {}, httpOptions, resource)
      .then(dto => {
        this.observer.onRetrySuccess(dto, localParams);
        return dto;
      })
      .catch(error => {
        this.observer.onRetryError(error, localParams);
        throw error;
      });
  }
}
